import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { PlayerGameScoreDialog } from '@sorare/marketplace/src/shared/stats/PlayerGameScoreDialog';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';
import { anyPlayerGameStatsToGenericGame } from '@sorare/gameplay/src/lib/anyPlayerGameStats';

import type {
  NbaPlayerPastFixturesQuery,
  NbaPlayerPastFixturesQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const NBA_PLAYER_FIXTURES_QUERY = gql`
  query NbaPlayerPastFixturesQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      anyGameStats(last: 30) {
        id
        ...anyPlayerGameStatsToGenericGame_anyPlayerGameStats
      }
      activeClub {
        slug
        upcomingAnyGames(first: 100) {
          id
          ...anyGameToGenericGame_anyGameInterface
        }
      }
    }
  }
  ${anyPlayerGameStatsToGenericGame.fragments.anyPlayerGameStats}
` as TypedDocumentNode<
  NbaPlayerPastFixturesQuery,
  NbaPlayerPastFixturesQueryVariables
>;

export const PlayerGames = () => {
  const { slug } = useTypedParams<PageParams>();
  const [playerGameScoreId, setPlayerGameScoreId] = useState<string>();

  const [tab, setTab] = useState<'past' | 'future'>('future');
  const { data, loading } = usePaginatedQuery(NBA_PLAYER_FIXTURES_QUERY, {
    variables: { slug },
    connection: 'GameConnection',
  });

  const pastPlayerGameStats =
    data?.anyPlayer?.anyGameStats.filter(Boolean) || [];
  const futureGames =
    data?.anyPlayer?.activeClub?.upcomingAnyGames.filter(Boolean) || [];

  if (tab === 'future' && futureGames.length <= 1 && !loading) {
    setTab('past');
  }

  return (
    <>
      <DumbMatches
        displayName={data?.anyPlayer?.displayName ?? ''}
        loading={loading}
        pastGames={pastPlayerGameStats.map(playerGameStats =>
          anyPlayerGameStatsToGenericGame({
            playerGameStats,
            onClick: id => setPlayerGameScoreId(idFromObject(id)),
          })
        )}
        futureGames={futureGames.map(anyGame =>
          anyGameToGenericGame({ anyGame })
        )}
        infiniteScrollLoader={null}
        tab={tab}
        setTab={setTab}
      />
      {!!playerGameScoreId && (
        <PlayerGameScoreDialog
          playerGameScoreId={playerGameScoreId}
          onClose={() => setPlayerGameScoreId(undefined)}
          open={!!playerGameScoreId}
          showUpsell={false}
        />
      )}
    </>
  );
};

export default PlayerGames;
